import React from 'react';
import { Typography, Table, Alert } from 'antd';
import LatexText from '../LatexText';

const { Title, Paragraph, Text } = Typography;

export type NoteBlock =
  | { type: 'heading'; text: string }
  | { type: 'paragraph'; text: string }
  | { type: 'bullet_list'; items: string[] }
  | { type: 'numbered_list'; items: string[] }
  | { type: 'table'; headers: string[]; rows: string[][] }
  | { type: 'callout'; style: 'info' | 'important' | 'tip'; text: string }
  | { type: 'definition'; term: string; explanation: string };

// Read-only render of a single block — used for the admin preview pane. Intentionally
// mirrors the visual language the mobile app's native block renderer uses (same block
// types, same idea of "each type gets its own component"), so what an admin approves
// here is a faithful preview of student-facing output, not just a generic JSON dump.
const CALLOUT_COLOR: Record<string, string> = { info: '#1D4ED8', important: '#DC2626', tip: '#059669' };

const NoteBlockRenderer: React.FC<{ block: NoteBlock }> = ({ block }) => {
  switch (block.type) {
    case 'heading':
      return <Title level={4} style={{ marginTop: 16 }}><LatexText text={block.text} /></Title>;
    case 'paragraph':
      return <Paragraph><LatexText text={block.text} /></Paragraph>;
    case 'bullet_list':
      return (
        <ul style={{ paddingLeft: 20 }}>
          {block.items.map((it, i) => <li key={i}><LatexText text={it} /></li>)}
        </ul>
      );
    case 'numbered_list':
      return (
        <ol style={{ paddingLeft: 20 }}>
          {block.items.map((it, i) => <li key={i}><LatexText text={it} /></li>)}
        </ol>
      );
    case 'table':
      return (
        <Table
          style={{ marginBottom: 16 }}
          size="small"
          pagination={false}
          columns={block.headers.map((h, i) => ({ title: h, dataIndex: String(i), key: String(i) }))}
          dataSource={block.rows.map((row, ri) => {
            const rec: Record<string, React.ReactNode> = { key: ri };
            row.forEach((cell, ci) => { rec[String(ci)] = <LatexText text={cell} />; });
            return rec;
          })}
        />
      );
    case 'callout':
      return (
        <Alert
          style={{ marginBottom: 12, borderColor: CALLOUT_COLOR[block.style] }}
          type={block.style === 'important' ? 'error' : block.style === 'tip' ? 'success' : 'info'}
          message={<LatexText text={block.text} />}
        />
      );
    case 'definition':
      return (
        <Paragraph style={{ marginBottom: 12 }}>
          <Text strong>{block.term}: </Text>
          <LatexText text={block.explanation} />
        </Paragraph>
      );
    default:
      return null;
  }
};

export default NoteBlockRenderer;
